import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'
import { exportQuoteToDocx } from '../lib/quoteExport'
import { sendQuoteAcceptanceLink } from '../lib/emailService'
import {
  ArrowLeft, Plus, Trash2, Save, FileDown,
  User, Calendar, MapPin, Tag, DollarSign, FileText, X,
  Search, Briefcase, Send,
} from 'lucide-react'

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_TERMS = `• A deposit is required to confirm your booking.
• Full payment is due prior to or on the day of the event.
• Cancellations made less than 14 days before the event date are non-refundable.
• Winnipeg Tent Rental is not responsible for damage caused by weather events beyond standard wear.
• Client is responsible for ensuring adequate space and ground conditions for setup.`

const EVENT_TYPES = ['wedding', 'corporate', 'birthday', 'festival', 'community', 'other']
const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'converted']
const SUCCESS_FEEDBACK_MS = 5000

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  return parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function calcTotals(lineItems, discountType, discountValue) {
  const subtotal = lineItems.reduce((s, li) => s + (parseFloat(li.quantity || 0) * parseFloat(li.unit_price || 0)), 0)
  let discountAmount = 0
  if (discountType === 'flat') discountAmount = Math.min(parseFloat(discountValue || 0), subtotal)
  if (discountType === 'percentage') discountAmount = subtotal * Math.min(parseFloat(discountValue || 0), 100) / 100
  const discountedSubtotal = subtotal - discountAmount
  const gst = discountedSubtotal * 0.05
  const pst = discountedSubtotal * 0.07
  const grandTotal = discountedSubtotal + gst + pst
  return { subtotal, discountAmount, discountedSubtotal, gst, pst, grandTotal }
}

// ─── Client Search Dropdown ──────────────────────────────────────────────────

function ClientSearch({ selectedClient, onSelect }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const timeout = setTimeout(async () => {
      setLoading(true)
      const { data } = await supabase
        .from('clients')
        .select('id, name, email, phone')
        .or(`name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`)
        .limit(8)
      setResults(data || [])
      setLoading(false)
      setOpen(true)
    }, 250)
    return () => clearTimeout(timeout)
  }, [query])

  function select(client) {
    onSelect(client)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selectedClient ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-sm)', background: 'white',
        }}>
          <div>
            <span style={{ fontWeight: 600 }}>{selectedClient.name}</span>
            {selectedClient.email && (
              <span style={{ marginLeft: 8, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {selectedClient.email}
              </span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onSelect(null)} title="Change client">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
          <input
            className="form-input"
            style={{ paddingLeft: 32 }}
            placeholder="Search client by name, email, or phone…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => query && setOpen(true)}
          />
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
          marginTop: 4, overflow: 'hidden',
        }}>
          {results.map(c => (
            <button
              key={c.id}
              onMouseDown={() => select(c)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 14px', border: 'none', background: 'none',
                cursor: 'pointer', borderBottom: '1px solid var(--color-border)',
                fontFamily: 'var(--font-body)',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--color-bg)'}
              onMouseOut={e => e.currentTarget.style.background = 'none'}
            >
              <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{c.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {[c.email, c.phone].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && query && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
          marginTop: 4, padding: '12px 14px',
          fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)',
        }}>
          No clients found for "{query}"
        </div>
      )}
    </div>
  )
}

// ─── Catalogue Picker ────────────────────────────────────────────────────────

function CataloguePicker({ onSelect, onClose }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    supabase.from('catalogue_items').select('*').eq('active', true).order('category').order('name')
      .then(({ data }) => {
        if (!mounted) return
        setItems(data || [])
      })
      .catch(err => {
        console.error('Failed to load catalogue items:', err)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => { mounted = false }
  }, [])

  const filtered = items.filter(i =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase())
  )

  const grouped = {}
  filtered.forEach(i => {
    if (!grouped[i.category]) grouped[i.category] = []
    grouped[i.category].push(i)
  })

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>Add from Catalogue</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Search items…"
              value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          </div>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading…</div>
          ) : Object.keys(grouped).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>No items found.</div>
          ) : Object.keys(grouped).map(cat => (
            <div key={cat}>
              <div style={{
                padding: '6px 20px', fontSize: 'var(--text-xs)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)',
                background: 'var(--color-bg)',
              }}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </div>
              {grouped[cat].map(item => (
                <button
                  key={item.id}
                  onClick={() => { onSelect(item); onClose() }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', textAlign: 'left', padding: '10px 20px',
                    border: 'none', background: 'none', cursor: 'pointer',
                    fontFamily: 'var(--font-body)', borderBottom: '1px solid var(--color-border)',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--color-bg)'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  <div>
                    <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{item.name}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>per {item.unit}</div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    ${fmtMoney(item.default_unit_price)}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function QuoteDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const quoteId = typeof id === 'string' && id.trim() ? id : null
  const isNew = !quoteId || quoteId === 'new'

  // Quote state
  const [client, setClient] = useState(null)
  const [eventDate, setEventDate] = useState('')
  const [eventType, setEventType] = useState('')
  const [address, setAddress] = useState('')
  const [status, setStatus] = useState('draft')
  const [discountType, setDiscountType] = useState('')
  const [discountValue, setDiscountValue] = useState('')
  const [depositRequired, setDepositRequired] = useState('')
  const [depositReceived, setDepositReceived] = useState('')
  const [depositReceivedDate, setDepositReceivedDate] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('pending')
  const [notes, setNotes] = useState('')
  const [terms, setTerms] = useState(DEFAULT_TERMS)
  const [quoteNumber, setQuoteNumber] = useState('')
  const [lineItems, setLineItems] = useState([])
  const [quote, setQuote] = useState(null)
  const [linkedJob, setLinkedJob] = useState(null)

  // Tracks the last status value that has been persisted to the DB.
  // Used by the auto-save effect to distinguish operator changes from
  // load-triggered state sets, so we never write on initial mount.
  const lastSavedStatusRef = useRef(null)

  // UI state
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showCatPicker, setShowCatPicker] = useState(false)
  const [prefillFromLead, setPrefillFromLead] = useState(false)

  // Load existing quote
  useEffect(() => {
    if (isNew) return
    async function load() {
      setLoading(true)
      const { data: q, error } = await supabase
        .from('quotes')
        .select('*, clients(*), quote_line_items(*)')
        .eq('id', quoteId)
        .single()

      if (error || !q) {
        navigate('/quotes')
        return
      }

      setQuote(q)
      setClient(q.clients)
      setEventDate(q.event_date || '')
      setEventType(q.event_type || '')
      setAddress(q.address || '')
      setStatus(q.status || 'draft')
      lastSavedStatusRef.current = q.status || 'draft'
      setDiscountType(q.discount_type || '')
      setDiscountValue(q.discount_value || '')
      setDepositRequired(q.deposit_required || '')
      setDepositReceived(q.deposit_received || '')
      setDepositReceivedDate(q.deposit_received_date || '')
      setPaymentStatus(q.payment_status || 'pending')
      setNotes(q.notes || '')
      setTerms(q.terms || DEFAULT_TERMS)
      setQuoteNumber(q.quote_number || '')
      setLineItems((q.quote_line_items || []).map(li => ({
        id: li.id,
        catalogue_item_id: li.catalogue_item_id || null,
        description: li.description,
        quantity: li.quantity,
        unit_price: li.unit_price,
      })))
      setLoading(false)
    }
    load()
  }, [id, isNew, navigate])

  // Fetch linked job when viewing a converted quote
  useEffect(() => {
    if (isNew || !quoteId || status !== 'converted') return
    supabase
      .from('jobs')
      .select('id')
      .eq('quote_id', quoteId)
      .maybeSingle()
      .then(({ data }) => setLinkedJob(data || null))
  }, [isNew, quoteId, status])

  // Auto-save status changes immediately when the operator picks a new status.
  // Guards:
  //   - skip new quotes (no DB row yet)
  //   - skip if status matches lastSavedStatusRef — this prevents firing on
  //     initial mount and on the load-triggered setStatus call
  useEffect(() => {
    if (isNew || !quoteId) return
    if (status === lastSavedStatusRef.current) return
    async function autoSaveStatus() {
      const { error: err } = await supabase
        .from('quotes')
        .update({ status })
        .eq('id', quoteId)
      if (err) {
        setError('Status update failed: ' + err.message)
      } else {
        lastSavedStatusRef.current = status
        setSuccess('Status saved.')
        setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
      }
    }
    autoSaveStatus()
  }, [status, isNew, quoteId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew) return

    const clientId = searchParams.get('client')
    const leadId = searchParams.get('lead')
    if (!clientId && !leadId) return

    async function prefill() {
      if (clientId) {
        const { data: c } = await supabase
          .from('clients')
          .select('id, name, email, phone, address')
          .eq('id', clientId)
          .maybeSingle()
        if (c) {
          setClient(c)
          if (c.address) setAddress(c.address)
        }
      }

      if (leadId) {
        const { data: l } = await supabase
          .from('leads')
          .select('id, event_date, event_type, notes')
          .eq('id', leadId)
          .maybeSingle()
        if (l) {
          if (l.event_date) setEventDate(l.event_date)
          if (l.event_type) setEventType(l.event_type)
          if (l.notes) setNotes(prev => prev || `Lead notes:\n${l.notes}`)
          setPrefillFromLead(true)
        }
      }
    }

    prefill()
  }, [isNew, searchParams])

  function addLineItem(catalogueItem) {
    setLineItems(prev => [...prev, {
      _key: Date.now(),
      catalogue_item_id: catalogueItem?.id || null,
      description: catalogueItem?.name || '',
      quantity: 1,
      unit_price: catalogueItem?.default_unit_price || 0,
    }])
  }

  function updateLineItem(index, field, value) {
    setLineItems(prev => prev.map((li, i) => i === index ? { ...li, [field]: value } : li))
  }

  function removeLineItem(index) {
    setLineItems(prev => prev.filter((_, i) => i !== index))
  }

  const totals = calcTotals(lineItems, discountType, discountValue)

  async function handleSave() {
    if (!client) return setError('Please select a client.')
    setSaving(true)
    setError('')
    setSuccess('')

    const payload = {
      client_id: client.id,
      event_date: eventDate || null,
      event_type: eventType || null,
      address: address || null,
      status,
      subtotal: totals.subtotal,
      discount_type: discountType || null,
      discount_value: parseFloat(discountValue) || 0,
      discount_amount: totals.discountAmount,
      gst: totals.gst,
      pst: totals.pst,
      grand_total: totals.grandTotal,
      deposit_required: parseFloat(depositRequired) || 0,
      deposit_received: parseFloat(depositReceived) || 0,
      deposit_received_date: depositReceivedDate || null,
      payment_status: paymentStatus,
      notes: notes || null,
      terms: terms || null,
    }

    let targetQuoteId = isNew ? null : quoteId

    if (isNew) {
      const { data, error: qErr } = await supabase
        .from('quotes')
        .insert(payload)
        .select()
        .single()
      if (qErr) { setSaving(false); return setError(qErr.message) }
      targetQuoteId = data.id
      setQuoteNumber(data.quote_number)
      setQuote(data)

      const leadId = searchParams.get('lead')
      if (leadId) {
        await supabase.from('leads').update({ status: 'quoted' }).eq('id', leadId)
      }
    } else {
      const { error: qErr } = await supabase
        .from('quotes')
        .update(payload)
        .eq('id', targetQuoteId)
      if (qErr) { setSaving(false); return setError(qErr.message) }
    }

    // Upsert line items: delete existing, insert all current
    await supabase.from('quote_line_items').delete().eq('quote_id', targetQuoteId)
    if (lineItems.length > 0) {
      const { error: liErr } = await supabase.from('quote_line_items').insert(
        lineItems.map(li => ({
          quote_id: targetQuoteId,
          catalogue_item_id: li.catalogue_item_id || null,
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
        }))
      )
      if (liErr) { setSaving(false); return setError(liErr.message) }
    }

    setSaving(false)
    setSuccess('Quote saved successfully.')
    if (isNew) navigate(`/quotes/${targetQuoteId}`, { replace: true })
    setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
  }

  async function handleExport() {
    if (!quote && isNew) return setError('Save the quote first before exporting.')
    if (!client) return setError('Client is required to export.')
    setExporting(true)
    try {
      await exportQuoteToDocx({
        quoteNumber: quoteNumber || 'DRAFT',
        eventDate,
        eventType,
        address,
        terms,
        notes,
        depositRequired: parseFloat(depositRequired) || 0,
        depositReceived: parseFloat(depositReceived) || 0,
        ...totals,
      }, lineItems, client)
    } catch (e) {
      setError('Export failed: ' + e.message)
    }
    setExporting(false)
  }

  async function handleConvertToJob() {
    if (!quote || !client) return
    setSaving(true)
    setError('')

    // Create job pre-filled from quote data
    const { data: newJob, error: jErr } = await supabase
      .from('jobs')
      .insert({
        client_id: client.id,
        quote_id: quote.id,
        event_date: eventDate || null,
        setup_date: eventDate || null,
        teardown_date: eventDate || null,
        address: address || null,
        event_type: eventType || null,
        status: 'confirmed',
        crew_required: 2,
        deposit_required: parseFloat(depositRequired) || 0,
        deposit_received: parseFloat(depositReceived) || 0,
        deposit_received_date: depositReceivedDate || null,
        payment_status: paymentStatus,
      })
      .select()
      .single()

    if (jErr) { setSaving(false); return setError(jErr.message) }

    // Update quote status to converted
    await supabase.from('quotes').update({ status: 'converted' }).eq('id', quote.id)
    setStatus('converted')
    setSaving(false)

    // Navigate to the new job so admin can add inventory and set crew
    navigate(`/jobs/${newJob.id}`, {
      state: { flashSuccess: 'Quote converted to job successfully.' }
    })
  }

  async function handleSendQuoteLink() {
    if (!quote || !client?.email) {
      setError('Client email is required to send the acceptance link.')
      return
    }
    setSaving(true)
    setError('')
    // Make sure quote is saved with current status before sending
    const result = await sendQuoteAcceptanceLink(
      {
        id: quote.id,
        quote_number: quote.quote_number,
        event_date: eventDate,
        event_type: eventType,
        address,
        grand_total: totals.grandTotal,
        deposit_required: depositRequired,
        acceptance_token: quote.acceptance_token,
      },
      client
    )
    if (!result.ok) {
      setError(`Email failed: ${result.error}`)
    } else {
      // Update status to 'sent' if currently draft
      if (status === 'draft') {
        await supabase.from('quotes').update({ status: 'sent' }).eq('id', quote.id)
        setStatus('sent')
      }
      setSuccess('Quote link sent to client!')
      setTimeout(() => setSuccess(''), 5000)
    }
    setSaving(false)
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Loading quote…</div>

  const balanceDue = Math.max(0, totals.grandTotal - (parseFloat(depositReceived) || 0))

  // ── Next Step panel config ────────────────────────────────────────────────
  // Derives the one most important action from the current quote state.
  // Kept as a plain object so the JSX stays declarative and easy to scan.
  const nextStep = (() => {
    if (isNew)
      return { bg: '#f8fafc', border: '#e2e8f0', color: '#475569',
               msg: 'Save this quote to continue.' }
    if (status === 'draft')
      return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af',
               msg: 'Review pricing and line items, then send this quote to the client using the button above.' }
    if (status === 'sent')
      return { bg: '#fefce8', border: '#fde68a', color: '#92400e',
               msg: "Waiting for the client's response. They can accept or decline via the quote link." }
    if (status === 'accepted')
      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534',
               msg: 'This quote has been accepted. Convert it into a job to start managing the booking.',
               action: 'convert' }
    if (status === 'converted')
      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534',
               msg: 'This quote has been booked. Manage the deposit and crew on the linked job.',
               action: 'view-job' }
    if (status === 'declined')
      return { bg: '#fafafa', border: '#e5e7eb', color: '#6b7280',
               msg: 'This quote was declined. Revise the pricing or terms and resend if needed.' }
    return null
  })()

  return (
    <div data-testid="quote-detail-page">
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: 'var(--sp-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/quotes')} title="Back to Quotes">
            <ArrowLeft size={16} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 className="page-title" style={{ fontSize: 'var(--text-xl)' }}>
                {isNew ? 'New Quote' : (quoteNumber || 'Quote')}
              </h1>
              {!isNew && (
                <span className={`badge badge-${status}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              )}
            </div>
            {!isNew && quote?.created_at && (
              <p className="page-subtitle">
                Created {format(new Date(quote.created_at), 'MMM d, yyyy')}
                {client ? ` · ${client.name}` : ''}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isNew && status === 'accepted' && (
            <button data-testid="quote-convert-button" className="btn btn-success" onClick={handleConvertToJob}>
              <Briefcase size={14} /> Convert to Job
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
            <FileDown size={14} /> {exporting ? 'Exporting…' : 'Export Word'}
          </button>
          <button data-testid="quote-save-button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Quote'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>}
      {success && <div className="alert alert-success" data-testid="feedback-banner-success" style={{ marginBottom: 'var(--sp-4)' }}>{success}</div>}
      {isNew && prefillFromLead && (
        <div className="alert alert-info" data-testid="quote-prefill-from-lead" style={{ marginBottom: 'var(--sp-4)' }}>
          Quote prefilled from lead details. Review and save when ready.
        </div>
      )}

      {!isNew && (
        <div className="card" data-testid="quote-status-section" style={{ marginBottom: 'var(--sp-4)' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700 }}>Quote Status</div>
            <span className={`badge badge-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
            <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
              <label className="form-label" style={{ marginBottom: 4 }}>Update Status</label>
              <select data-testid="quote-status-select" className="form-select" value={status}
                onChange={e => setStatus(e.target.value)}>
                {QUOTE_STATUSES.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div data-testid="quote-form" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 'var(--sp-5)', alignItems: 'start' }}>

        {/* ── Left Column ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>

          {/* Section: Client */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <User size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Client</span>
              </div>
            </div>
            <div className="card-body">
              <ClientSearch selectedClient={client} onSelect={setClient} />
              {!client && (
                <p style={{ marginTop: 8, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  Search your client list, or{' '}
                  <a href="/clients" onClick={e => { e.preventDefault(); navigate('/clients') }}>
                    go to Clients
                  </a>
                  {' '}to create a new client first.
                </p>
              )}
            </div>
          </div>

          {/* Section: Event Details */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Event Details</span>
              </div>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Event Date</label>
                  <input className="form-input" type="date" value={eventDate}
                    onChange={e => setEventDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <select className="form-select" value={eventType}
                    onChange={e => setEventType(e.target.value)}>
                    <option value="">Select type…</option>
                    {EVENT_TYPES.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <MapPin size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Delivery / Setup Address
                </label>
                <input className="form-input" value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="123 Main St, Winnipeg, MB" />
              </div>
            </div>
          </div>

          {/* Section: Line Items */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Line Items</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCatPicker(true)}>
                  <Plus size={13} /> From Catalogue
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => addLineItem(null)}>
                  <Plus size={13} /> Custom
                </button>
              </div>
            </div>

            {lineItems.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                <Tag size={28} style={{ opacity: 0.25 }} />
                <p style={{ fontSize: 'var(--text-sm)' }}>No items yet. Add from catalogue or enter a custom line.</p>
                <button className="btn btn-primary btn-sm" onClick={() => setShowCatPicker(true)}>
                  <Plus size={13} /> Add from Catalogue
                </button>
              </div>
            ) : (
              <div>
                {/* Line items header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 80px 110px 100px 36px',
                  gap: 8, padding: '8px 20px', borderBottom: '1px solid var(--color-border)',
                  fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: 'var(--color-text-muted)',
                }}>
                  <span>Description</span>
                  <span style={{ textAlign: 'right' }}>Qty</span>
                  <span style={{ textAlign: 'right' }}>Unit Price</span>
                  <span style={{ textAlign: 'right' }}>Total</span>
                  <span></span>
                </div>

                {lineItems.map((li, i) => {
                  const lineTotal = parseFloat(li.quantity || 0) * parseFloat(li.unit_price || 0)
                  return (
                    <div key={li.id || li._key || i} style={{
                      display: 'grid', gridTemplateColumns: '1fr 80px 110px 100px 36px',
                      gap: 8, padding: '8px 20px', borderBottom: '1px solid var(--color-border)',
                      alignItems: 'center',
                    }}>
                      <input
                        className="form-input"
                        style={{ fontSize: 'var(--text-sm)' }}
                        value={li.description}
                        onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="Description…"
                      />
                      <input
                        className="form-input"
                        style={{ fontSize: 'var(--text-sm)', textAlign: 'right' }}
                        type="number" min="0" step="1"
                        value={li.quantity}
                        onChange={e => updateLineItem(i, 'quantity', e.target.value)}
                      />
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>$</span>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-sm)', paddingLeft: 20, textAlign: 'right' }}
                          type="number" min="0" step="0.01"
                          value={li.unit_price}
                          onChange={e => updateLineItem(i, 'unit_price', e.target.value)}
                        />
                      </div>
                      <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                        ${fmtMoney(lineTotal)}
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--color-red)', padding: 4 }}
                        onClick={() => removeLineItem(i)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )
                })}

                {/* Add row buttons */}
                <div style={{ padding: '10px 20px', display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowCatPicker(true)}>
                    <Plus size={12} /> From Catalogue
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => addLineItem(null)}>
                    <Plus size={12} /> Custom Line
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Section: Discount */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Discount</span>
              </div>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Discount Type</label>
                  <select className="form-select" value={discountType}
                    onChange={e => { setDiscountType(e.target.value); setDiscountValue('') }}>
                    <option value="">No discount</option>
                    <option value="flat">Flat ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                {discountType && (
                  <div className="form-group">
                    <label className="form-label">
                      {discountType === 'flat' ? 'Discount Amount ($)' : 'Discount Percentage (%)'}
                    </label>
                    <input
                      className="form-input"
                      type="number" min="0" step={discountType === 'flat' ? '1' : '0.1'}
                      value={discountValue}
                      onChange={e => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'flat' ? '0.00' : '0'}
                    />
                  </div>
                )}
              </div>
              {discountType && totals.discountAmount > 0 && (
                <div style={{ marginTop: 8, fontSize: 'var(--text-sm)', color: 'var(--color-green)', fontWeight: 600 }}>
                  Discount applied: −${fmtMoney(totals.discountAmount)}
                </div>
              )}
            </div>
          </div>

          {/* Section: Notes & Terms */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Notes & Terms</span>
              </div>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              <div className="form-group">
                <label className="form-label">Internal Notes</label>
                <textarea className="form-textarea" rows={3} value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Admin notes (not visible on exported document)…" />
              </div>
              <div className="form-group">
                <label className="form-label">Terms & Conditions</label>
                <textarea className="form-textarea" rows={6} value={terms}
                  onChange={e => setTerms(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', position: 'sticky', top: 24 }}>

          {/* Totals Card */}
          <div className="card">
            <div className="card-header" style={{ padding: '14px 18px' }}>
              <span style={{ fontWeight: 700 }}>Quote Totals</span>
            </div>
            <div className="card-body" style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Subtotal', val: totals.subtotal },
                  ...(totals.discountAmount > 0 ? [{ label: `Discount${discountType === 'percentage' ? ` (${discountValue}%)` : ''}`, val: -totals.discountAmount, neg: true }] : []),
                  { label: 'After Discount', val: totals.discountedSubtotal, sub: true },
                  { label: 'GST (5%)', val: totals.gst },
                  { label: 'PST (7%)', val: totals.pst },
                ].map(({ label, val, neg, sub }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: sub ? 0.5 : 1 }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{label}</span>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                      color: neg ? 'var(--color-green)' : undefined,
                    }}>
                      {neg ? '−' : ''}${fmtMoney(Math.abs(val))}
                    </span>
                  </div>
                ))}
                <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: 10, marginTop: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>Grand Total</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-lg)' }}>
                      ${fmtMoney(totals.grandTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Deposit & Payment Card */}
          <div className="card">
            <div className="card-header" style={{ padding: '14px 18px' }}>
              <span style={{ fontWeight: 700 }}>Deposit & Payment</span>
            </div>
            <div className="card-body" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              {status === 'converted' ? (
                /* ── Converted: payment is now owned by the job ── */
                <>
                  <div style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 'var(--radius-sm)',
                    padding: '12px 14px',
                    fontSize: 'var(--text-sm)',
                    color: '#1e40af',
                    lineHeight: 1.5,
                  }}>
                    Payment is now tracked on the job for this booking. Edit deposit and payment details there.
                  </div>
                  {linkedJob && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/jobs/${linkedJob.id}`)}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      <Briefcase size={13} /> View Job →
                    </button>
                  )}
                </>
              ) : (
                /* ── Active quote: editable payment fields ── */
                <>
                  <div className="form-group">
                    <label className="form-label">Deposit Required ($)</label>
                    <input className="form-input" type="number" min="0" step="0.01"
                      value={depositRequired}
                      onChange={e => setDepositRequired(e.target.value)}
                      placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Deposit Received ($)</label>
                    <input className="form-input" type="number" min="0" step="0.01"
                      value={depositReceived}
                      onChange={e => setDepositReceived(e.target.value)}
                      placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date Received</label>
                    <input className="form-input" type="date"
                      value={depositReceivedDate}
                      onChange={e => setDepositReceivedDate(e.target.value)} />
                  </div>

                  {/* Balance Due */}
                  <div style={{
                    background: balanceDue > 0 ? '#fef3c7' : '#d1fae5',
                    borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: balanceDue > 0 ? '#92400e' : '#065f46' }}>
                      Balance Due
                    </span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: balanceDue > 0 ? '#92400e' : '#065f46' }}>
                      ${fmtMoney(balanceDue)}
                    </span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Payment Status</label>
                    <select className="form-select" value={paymentStatus}
                      onChange={e => setPaymentStatus(e.target.value)}>
                      <option value="pending">Pending</option>
                      <option value="deposit_paid">Deposit Paid</option>
                      <option value="paid_in_full">Paid in Full</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Card */}
          {!isNew && (
            <div className="card">
              <div className="card-header" style={{ padding: '14px 18px' }}>
                <span style={{ fontWeight: 700 }}>Quote Status</span>
              </div>
              <div className="card-body" style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Current status</span>
                <span className={`badge badge-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
              </div>
                {quote?.accepted_at && (
                  <div style={{ marginTop: 10, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    Accepted: {format(new Date(quote.accepted_at), 'MMM d, yyyy h:mm a')}
                  </div>
                )}
                {linkedJob && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => navigate(`/jobs/${linkedJob.id}`)}
                    style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
                  >
                    <Briefcase size={13} /> View Job →
                  </button>
                )}
                {quote?.acceptance_token && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      className="btn btn-sm"
                      onClick={handleSendQuoteLink}
                      disabled={saving || !client?.email || status === 'converted'}
                      style={{
                        width: '100%', background: 'var(--color-navy)',
                        color: 'white', border: 'none', justifyContent: 'center',
                      }}
                      title={!client?.email ? 'Client email required' : 'Send quote link to client'}
                    >
                      <Send size={13} /> Send Quote to Client
                    </button>
                    {!client?.email && (
                      <div style={{ fontSize: 'var(--text-xs)', color: '#92400E', marginTop: 4 }}>
                        Add client email first to send the link.
                      </div>
                    )}
                    <div style={{
                      marginTop: 8, background: 'var(--color-bg)', borderRadius: 4, padding: '6px 8px',
                      fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)',
                      wordBreak: 'break-all',
                    }}>
                      {process.env.REACT_APP_APP_URL || 'http://localhost:3000'}/quote-accept/{quote.acceptance_token}
                    </div>
                  </div>
                )}
                {client?.id && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                  >
                    <User size={13} /> View Client →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Next Step Panel */}
          {nextStep && (
            <div className="card">
              <div className="card-header" style={{ padding: '12px 18px' }}>
                <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>Next Step</span>
              </div>
              <div className="card-body" style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{
                  background: nextStep.bg,
                  border: `1px solid ${nextStep.border}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                  fontSize: 'var(--text-sm)',
                  color: nextStep.color,
                  lineHeight: 1.5,
                }}>
                  {nextStep.msg}
                </div>
                {nextStep.action === 'convert' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleConvertToJob}
                    disabled={saving}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <Briefcase size={13} /> Convert to Job
                  </button>
                )}
                {nextStep.action === 'view-job' && linkedJob && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(`/jobs/${linkedJob.id}`)}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <Briefcase size={13} /> View Job →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Save Quote */}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ width: '100%' }}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Quote'}
          </button>
        </div>
      </div>

      {/* Catalogue Picker Modal */}
      {showCatPicker && (
        <CataloguePicker
          onSelect={item => addLineItem(item)}
          onClose={() => setShowCatPicker(false)}
        />
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          .quote-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}
